import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './lib/supabase';
import LandingPage      from './components/LandingPage';
import LoginScreen      from './components/LoginScreen';
import HomeScreen       from './components/HomeScreen';
import EssayScreen      from './components/EssayScreen';
import TestMode         from './components/TestMode';
import TeacherPanel     from './components/TeacherPanel';
import Dashboard        from './components/Dashboard';
import Onboarding       from './components/Onboarding';
import LeadCapture      from './components/LeadCapture';
import LiveSession      from './components/LiveSession';
import PlacementTest    from './components/PlacementTest';
import CoursesPage      from './components/CoursesPage';
import IELTSDashboard   from './components/IELTSDashboard';
import ForgotPassword   from './components/ForgotPassword';
import TermsPage        from './components/TermsPage';
import PrivacyPage      from './components/PrivacyPage';
import { ResultsScreen, EssaysScreen } from './components/GalleryScreens';

// Academy
import AcademyHome      from './components/academy/AcademyHome';
import TeacherLogin     from './components/academy/TeacherLogin';
import TeacherDashboard from './components/academy/TeacherDashboard';
import StudentLogin     from './components/academy/StudentLogin';
import StudentRegister  from './components/academy/StudentRegister';
import StudentDashboard from './components/academy/StudentDashboard';
import BookTeacher      from './components/academy/BookTeacher';
import { loadTeacherSession, clearTeacherSession, loadStudentSession, clearStudentSession, saveTeacherSession, saveStudentSession } from './components/academy/academyUtils';

import {
  loadBranding, saveBranding,
  loadTiers,    saveTiers,
  loadCandidates, saveCandidates,
  loadPrompts,  savePrompts,
  loadFormulas, saveFormulas,
  loadResults,  saveResults,
  loadEssays,   saveEssays,
  loadSession,  saveSession, clearSession,
} from './data/storage';

const ONBOARDING_KEY = 'ic_onboarded_';
const _sessionCode = new URLSearchParams(window.location.search).get('session');

function StudentEntry() {
  return <LiveSession isTeacher={false} initialCode={_sessionCode} />;
}

// ── IELTS subscription types ─────────────────────────────────────────────────
const IELTS_SUB_TYPES = new Set([
  'standard','premium','trial',
  'ielts-standard','ielts-premium',
  'ielts_standard','ielts_premium',
]);

// Screens that authenticated users must never land on via browser back/forward
const PUBLIC_SCREENS = new Set([
  'landing','login','login-trial','academy',
  'student-login','student-register','teacher-login',
]);

function AppShell() {
  const _s = loadSession();

  // Restore __academyStudentId on page refresh so feedback.js can save essay_scores
  if (_s?.candidate?.isAcademyStudent) {
    const _stu = loadStudentSession();
    if (_stu?.id) window.__academyStudentId = _stu.id;
  }

  // ── Navigation stack ────────────────────────────────────────────────────────
  // go(s)      — push a new screen (forward navigation)
  // back()     — pop the current screen (back navigation); stays at root if already there
  // resetTo(s) — clear the entire stack and start fresh (used for login / logout)
  //
  // back() always returns to the ACTUAL previous screen regardless of how the
  // user arrived, eliminating all hardcoded back destinations.
  const [history, setHistory] = useState(() => {
    if (!_s) return ['landing'];
    if (_s.role === 'teacher') return ['teacher'];
    const _stu = loadStudentSession();
    if (_s.candidate?.isAcademyStudent && _stu && IELTS_SUB_TYPES.has(_stu.subscription_type)) {
      return ['ielts-dashboard'];
    }
    return ['home'];
  });

  const screen     = history[history.length - 1];
  const prevScreen = history.length >= 2 ? history[history.length - 2] : null;

  // Capture initial screen once for the mount replaceState (ref is stable across renders)
  const _initScreen = useRef(history[history.length - 1]);

  // Auth ref — lets the popstate handler (registered once on mount) read current auth state.
  // Initialized from session data so it's correct before the sync effects fire.
  const _authRef = useRef({
    candidate:      _s?.candidate || null,
    isTeacher:      _s?.role === 'teacher',
    academyStudent: loadStudentSession(),
  });

  // Tracks the live app screen so the guard can re-assert it via pushState
  // (direction-agnostic: works for both back and forward presses)
  const _currentScreenRef = useRef(screen);

  // Tracks the live history array so onPopState can read it synchronously
  // without a stale closure (the handler is registered once on mount)
  const _historyRef = useRef(history);

  function go(s) {
    setHistory(h => [...h, s]);
    window.history.pushState({ screen: s }, '', '#/' + s);
  }
  function back() {
    setHistory(h => h.length > 1 ? h.slice(0, -1) : h);
  }
  function resetTo(s) {
    setHistory([s]);
    window.history.replaceState({ screen: s }, '', '#/' + s);
  }

  // ── App state ───────────────────────────────────────────────────────────────
  const [candidate, setCandidate] = useState(_s?.candidate || null);
  const [isTeacher, setIsTeacher] = useState(_s?.role === 'teacher');
  const [showOnboarding,  setShowOnboarding]  = useState(false);
  const [showLeadCapture, setShowLeadCapture] = useState(false);
  const [leadType,        setLeadType]        = useState('smart');

  const [branding,   setBranding]   = useState(loadBranding());
  // prompts declared here so setPrompts is in scope for the useEffect below
  const [prompts,    setPrompts]    = useState(loadPrompts());

  // On mount: fetch branding and prompts from Supabase — always takes priority over localStorage
  useEffect(() => {
    supabase.from('academy_settings').select('setting_key,setting_value').order('updated_at', { ascending: false }).then(({ data }) => {
      if (!data || data.length === 0) return;
      const map = {};
      // Rows are newest-first; first occurrence of each key wins
      data.forEach(r => { if (!(r.setting_key in map)) map[r.setting_key] = r.setting_value; });
      setBranding(b => ({
        ...b,
        ...(map.logo_url      !== undefined ? { logo_url: map.logo_url }           : {}),
        ...(map.academy_name  !== undefined ? { logo: map.academy_name }           : {}),
        ...(map.tagline       !== undefined ? { tagline: map.tagline }             : {}),
        ...(map.primary_color !== undefined ? { primaryColor: map.primary_color }  : {}),
      }));
    });
    // Fetch prompts — Supabase is authoritative; fall back to localStorage when empty
    supabase.from('prompts').select('data').order('updated_at', { ascending:false }).limit(1).then(({ data }) => {
      if (data?.[0]?.data) { setPrompts(data[0].data); savePrompts(data[0].data); }
    });
  }, []);

  // ── Browser history sync ────────────────────────────────────────────────────
  // Stamps the initial screen into browser history so the back button has an
  // entry to return to, then keeps browser history in sync via go()/resetTo().
  // Hash prefix '#/' avoids colliding with LandingPage anchor links (#home etc).
  useEffect(() => {
    window.history.replaceState({ screen: _initScreen.current }, '', '#/' + _initScreen.current);

    function onPopState(e) {
      // e.state is null when navigating past the app's first entry or via anchor links
      const target = e.state?.screen ?? 'landing';
      const { candidate: _c, isTeacher: _t, academyStudent: _as } = _authRef.current;
      const isLoggedIn = !!(_c || _t || _as);

      // Guard 1: logged-in user about to land on a public/pre-login screen
      // (back or forward). Re-assert current screen — direction-agnostic.
      if (isLoggedIn && PUBLIC_SCREENS.has(target)) {
        const cur = _currentScreenRef.current;
        window.history.pushState({ screen: cur }, '', '#/' + cur);
        return;
      }

      // Compute the new history stack synchronously from the ref so we never
      // call setHistory with a stale-closure value.
      const prev = _historyRef.current;
      const idx  = prev.lastIndexOf(target);

      if (idx >= 0) {
        // Normal back/forward to a screen that exists in the app history stack
        setHistory(prev.slice(0, idx + 1));
        return;
      }

      // Guard 2: target not in app history stack.
      // Logged-in: stay put and re-assert current screen (forward into an
      // out-of-sync entry, e.g. 'home' after login wiped the old stack).
      // Logged-out: reset to landing (the safe unauthenticated default).
      if (isLoggedIn) {
        const cur = _currentScreenRef.current;
        window.history.pushState({ screen: cur }, '', '#/' + cur);
      } else {
        setHistory(['landing']);
      }
    }

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []); // runs once on mount

  const [tiers,      setTiers]      = useState(loadTiers());
  const [candidates, setCandidates] = useState(loadCandidates());
  const [formulas,   setFormulas]   = useState(loadFormulas());
  const [results,    setResults]    = useState(loadResults());
  const [essays,     setEssays]     = useState(loadEssays());

  const [activePrompt,    setActivePrompt]    = useState(null);
  const [activeEssayType, setActiveEssayType] = useState(null);

  // Academy session state
  const [academyTeacher, setAcademyTeacher] = useState(() => loadTeacherSession());
  const [academyStudent, setAcademyStudent] = useState(() => loadStudentSession());

  // Keep _authRef current so the popstate guard always sees live auth state
  useEffect(() => { _authRef.current.candidate      = candidate;      }, [candidate]);
  useEffect(() => { _authRef.current.isTeacher      = isTeacher;      }, [isTeacher]);
  useEffect(() => { _authRef.current.academyStudent = academyStudent; }, [academyStudent]);
  // Keep _currentScreenRef and _historyRef current after every navigation
  useEffect(() => { _currentScreenRef.current = screen;  }, [screen]);
  useEffect(() => { _historyRef.current       = history; }, [history]);

  // ── Student home resolver ────────────────────────────────────────────────────
  // Used only for auth redirects (login / logout), not for back navigation.
  function studentHome() {
    if (academyStudent) {
      return IELTS_SUB_TYPES.has(academyStudent.subscription_type) ? 'ielts-dashboard' : 'student-dashboard';
    }
    return candidate ? 'home' : 'landing';
  }

  // ── Auth handlers ────────────────────────────────────────────────────────────
  function handleCandidateLogin(c) {
    saveSession(c, 'candidate');
    setCandidate(c); setIsTeacher(false); resetTo('home');
    const key = ONBOARDING_KEY + c.email;
    if (!localStorage.getItem(key)) setShowOnboarding(true);
  }
  function handleStartTrial(name, email) {
    const e = email.trim().toLowerCase();
    let c = candidates.find(x => x.email.toLowerCase() === e);
    if (!c) {
      c = { email: e, name: name || 'New Student', tier: 'trial', usedSmart: 0, usedTest: 0, active: true };
      const updated = [...candidates, c];
      setCandidates(updated); saveCandidates(updated);
    }
    localStorage.setItem('ic_trial_used', Date.now().toString());
    handleCandidateLogin(c);
  }
  function handleTeacherLogin() {
    const tc = { name:'Mr. Ahmed Salem', tier:'premium', used:0, email:'teacher@ieltscompass' };
    saveSession(tc, 'teacher');
    setIsTeacher(true); setCandidate(tc); resetTo('teacher');
  }
  function handleLogout() {
    clearSession(); setCandidate(null); setIsTeacher(false); resetTo('landing');
  }

  function handleSelectPrompt(prompt, type)   { setActivePrompt(prompt); setActiveEssayType(type); go('essay'); }
  function handleSelectTestMode(prompt, type) { setActivePrompt(prompt); setActiveEssayType(type); go('test'); }

  function handleOnboardingComplete() {
    if (candidate) localStorage.setItem(ONBOARDING_KEY + candidate.email, '1');
    setShowOnboarding(false);
  }

  function handleIncrementUsed(mode) {
    const field = mode === 'test' ? 'usedTest' : 'usedSmart';
    const updated = candidates.map(c =>
      c.email.toLowerCase() === candidate.email.toLowerCase()
        ? { ...c, [field]: (c[field] || 0) + 1 } : c
    );
    const updatedCandidate = updated.find(c => c.email.toLowerCase() === candidate.email.toLowerCase());
    setCandidates(updated); saveCandidates(updated);
    if (updatedCandidate) { setCandidate(updatedCandidate); saveSession(updatedCandidate, 'candidate'); }
  }

  function hSaveBranding(v)   { setBranding(v);   saveBranding(v); }
  function hSaveTiers(v)      { setTiers(v);       saveTiers(v); }
  function hSaveCandidates(v) { setCandidates(v);  saveCandidates(v); }
  function hSavePrompts(v) {
    setPrompts(v); savePrompts(v);
    supabase.from('prompts').upsert(
      { id:'00000000-0000-0000-0000-000000000001', data:v, updated_at:new Date().toISOString() }
    ).then(({ error }) => {
      if (error) console.error('prompts upsert error:', error);
    });
  }
  function hSaveFormulas(v)   { setFormulas(v);    saveFormulas(v); }
  function hSaveResults(v)    { setResults(v);     saveResults(v); }
  function hSaveEssays(v)     { setEssays(v);      saveEssays(v); }

  // Academy auth handlers
  function handleAcademyTeacherLogin(t) {
    setAcademyTeacher(t); saveTeacherSession(t); resetTo('teacher-dashboard');
  }
  function handleAcademyTeacherLogout() {
    clearTeacherSession(); setAcademyTeacher(null); resetTo('landing');
  }
  function handleAcademyStudentLogin(s) {
    setAcademyStudent(s);
    saveStudentSession(s);
    if (IELTS_SUB_TYPES.has(s.subscription_type)) {
      window.__academyStudentId = s.id;
      const asCand = {
        id: s.id, name: s.name, email: s.email,
        tier: s.subscription_type?.includes('premium') ? 'premium' : 'standard',
        usedSmart: 0, usedTest: 0, active: true, isAcademyStudent: true,
      };
      setCandidate(asCand);
      saveSession(asCand, 'candidate');
      resetTo('ielts-dashboard');
    } else {
      resetTo('student-dashboard');
    }
  }
  function handleAcademyStudentLogout() {
    window.__academyStudentId = null;
    clearStudentSession(); clearSession();
    setAcademyStudent(null); setCandidate(null); setIsTeacher(false);
    resetTo('landing');
  }

  // ── ROUTES ──────────────────────────────────────────────────────────────────

  // ── Public / auth screens ──────────────────────────────────────────────────

  if (screen === 'landing') return (
    <LandingPage
      onLogin={()    => go('login')}
      onRegister={() => go('student-register')}
      onTrial={()    => go('login-trial')}
      onTerms={()    => go('terms')}
      onPrivacy={()  => go('privacy')}
      onAcademy={()  => go('academy')}
      branding={branding}
    />
  );

  if (screen === 'login') return (
    <LoginScreen
      branding={branding}
      onCandidateLogin={handleCandidateLogin}
      onTeacherLogin={handleTeacherLogin}
      onStartTrial={handleStartTrial}
      onPlacementTest={()       => go('placement')}
      onCourses={()             => go('courses')}
      onAcademy={()             => go('academy')}
      onTeacherPortal={()       => go('teacher-login')}
      onAcademyLogin={handleAcademyStudentLogin}
      onAcademyTeacherLogin={handleAcademyTeacherLogin}
      onForgotPassword={()      => go('forgot-password')}
      onTerms={()               => go('terms')}
      onPrivacy={()             => go('privacy')}
      onLanding={()             => resetTo('landing')}
    />
  );

  if (screen === 'login-trial') return (
    <LoginScreen
      branding={branding} autoTrial
      onCandidateLogin={handleCandidateLogin}
      onTeacherLogin={handleTeacherLogin}
      onStartTrial={handleStartTrial}
      onPlacementTest={()       => go('placement')}
      onCourses={()             => go('courses')}
      onAcademy={()             => go('academy')}
      onTeacherPortal={()       => go('teacher-login')}
      onAcademyLogin={handleAcademyStudentLogin}
      onAcademyTeacherLogin={handleAcademyTeacherLogin}
      onForgotPassword={()      => go('forgot-password')}
      onTerms={()               => go('terms')}
      onPrivacy={()             => go('privacy')}
      onLanding={()             => resetTo('landing')}
    />
  );

  if (screen === 'forgot-password') return <ForgotPassword onBack={back}/>;
  if (screen === 'terms')           return <TermsPage      onBack={back}/>;
  if (screen === 'privacy')         return <PrivacyPage    onBack={back}/>;
  if (screen === 'placement')       return <PlacementTest  onBack={back}/>;
  // courses and placement no longer need -home variants; back() handles the correct destination
  if (screen === 'courses')         return <CoursesPage    onBack={back} onPlacementTest={() => go('placement')}/>;
  // legacy aliases — redirect to canonical routes so back() still works correctly
  if (screen === 'placement-home')  return <PlacementTest  onBack={back}/>;
  if (screen === 'courses-home')    return <CoursesPage    onBack={back} onPlacementTest={() => go('placement')}/>;

  // ── Academy screens ────────────────────────────────────────────────────────

  if (screen === 'academy') return (
    <AcademyHome
      onBack={back}
      onBook={() => {
        const s = academyStudent;
        window.open(`https://wa.me/201008182425?text=${encodeURIComponent('Hello Mr. Ahmed Salem! I would like to book lessons. My name is ' + (s?.name || 'a new student') + '.')}`, '_blank');
      }}
      onStudentLogin={() => academyStudent ? resetTo(studentHome()) : go('student-login')}
      onRegister={() => go('student-register')}
      onTeacherLogin={() => go('teacher-login')}
      onPlacementTest={() => go('placement')}
    />
  );

  if (screen === 'teacher-login') return (
    <TeacherLogin
      onSuccess={handleAcademyTeacherLogin}
      onAdminSuccess={handleTeacherLogin}
      onBack={back}
    />
  );

  if (screen === 'teacher-dashboard') {
    const t = academyTeacher || loadTeacherSession();
    if (!t) { setHistory(['teacher-login']); return null; }
    return (
      <TeacherDashboard
        teacher={t}
        onLogout={handleAcademyTeacherLogout}
        onBack={() => resetTo('landing')}
      />
    );
  }

  if (screen === 'student-login') return (
    <StudentLogin
      onSuccess={handleAcademyStudentLogin}
      onRegister={() => go('student-register')}
      onBack={back}
      onForgotPassword={() => go('forgot-password')}
    />
  );

  if (screen === 'student-register') return (
    <StudentRegister
      onSuccess={handleAcademyStudentLogin}
      onLogin={() => go('student-login')}
      onBack={back}
    />
  );

  if (screen === 'book-teacher') return (
    <BookTeacher
      student={academyStudent || loadStudentSession()}
      onBack={back}
      onLogout={handleAcademyStudentLogout}
    />
  );

  // ── Student dashboards ─────────────────────────────────────────────────────

  if (screen === 'ielts-dashboard') {
    const s = academyStudent || loadStudentSession();
    if (!s) { setHistory(['student-login']); return null; }
    return (
      <IELTSDashboard
        student={s}
        onLogout={handleAcademyStudentLogout}
        onWritingTool={()       => go('home')}
        onProgressTracker={()  => go('dashboard')}
        onPlacementTest={()    => go('placement')}
        onAcademy={()          => go('academy')}
      />
    );
  }

  if (screen === 'student-dashboard') {
    const s = academyStudent || loadStudentSession();
    if (!s) { setHistory(['student-login']); return null; }
    // IELTS students are always redirected to their unified dashboard
    if (IELTS_SUB_TYPES.has(s.subscription_type)) { setHistory(['ielts-dashboard']); return null; }
    return (
      <StudentDashboard
        student={s}
        onLogout={handleAcademyStudentLogout}
        onBook={() => {
          const st = academyStudent || loadStudentSession();
          window.open(`https://wa.me/201008182425?text=${encodeURIComponent('Hello Mr. Ahmed Salem! I would like to book a session. My name is ' + (st?.name||'') + ' and my WhatsApp is ' + (st?.whatsapp||'') + '.')}`, '_blank');
        }}
        onPlacementTest={() => go('placement')}
        onAcademy={()        => go('academy')}
        onCourses={()        => go('courses')}
      />
    );
  }

  // ── Core IELTS writing screens ─────────────────────────────────────────────

  if (screen === 'teacher') return (
    <TeacherPanel
      branding={branding} tiers={tiers} candidates={candidates} prompts={prompts} formulas={formulas}
      onSaveBranding={hSaveBranding} onSaveTiers={hSaveTiers} onSaveCandidates={hSaveCandidates}
      onSavePrompts={hSavePrompts} onSaveFormulas={hSaveFormulas}
      onBack={back}
      onLogout={handleLogout}
    />
  );

  if (screen === 'essay' && activePrompt) return (
    <EssayScreen
      branding={branding} candidate={candidate} prompt={activePrompt}
      essayType={activeEssayType} formula={formulas[activeEssayType?.id]}
      onBack={back} onUsed={handleIncrementUsed}
    />
  );

  if (screen === 'test' && activePrompt) return (
    <TestMode
      branding={branding} candidate={candidate} prompt={activePrompt}
      essayType={activeEssayType}
      onBack={back} onUsed={handleIncrementUsed}
    />
  );

  if (screen === 'results') return (
    <ResultsScreen branding={branding} results={results} isTeacher={isTeacher} onBack={back} onSave={hSaveResults}/>
  );
  if (screen === 'essays') return (
    <EssaysScreen branding={branding} essays={essays} isTeacher={isTeacher} onBack={back} onSave={hSaveEssays}/>
  );

  // Dashboard back label derived from the actual previous screen in the stack
  const dashboardBackLabel =
    prevScreen === 'ielts-dashboard' ? 'My Dashboard' :
    prevScreen === 'home'            ? 'Writing Task 2' : 'Back';
  if (screen === 'dashboard') return (
    <Dashboard
      branding={branding} candidate={candidate} tiers={tiers}
      onBack={back} backLabel={dashboardBackLabel}
    />
  );

  // ── HomeScreen — IELTS writing tool ─────────────────────────────────────────
  const isAcademyIELTS = !!(academyStudent && IELTS_SUB_TYPES.has(academyStudent.subscription_type));

  return (
    <>
      <HomeScreen
        branding={branding} candidate={candidate} tiers={tiers} prompts={prompts}
        onSelectPrompt={handleSelectPrompt}
        onSelectTestMode={handleSelectTestMode}
        onLogout={handleLogout}
        onAdmin={isTeacher ? () => go('teacher') : null}
        onResults={()       => go('results')}
        onEssays={()        => go('essays')}
        onDashboard={()     => go('dashboard')}
        onPlacementTest={() => go('placement')}
        onCourses={()       => go('courses')}
        onAcademy={()       => go('academy')}
        onShowLead={(type)  => { setLeadType(type || 'smart'); setShowLeadCapture(true); }}
        onBack={isAcademyIELTS ? () => resetTo('ielts-dashboard') : null}
      />
      {showOnboarding  && <Onboarding  onComplete={handleOnboardingComplete}/>}
      {showLeadCapture && <LeadCapture onClose={() => setShowLeadCapture(false)} trialType={leadType}/>}
    </>
  );
}

export default function App() {
  if (_sessionCode) return <StudentEntry />;
  return <AppShell />;
}
